import withLabel from 'peach/decorators/withLabel/withLabel'

import { R } from 'core/helpers'
import { PendingEventHandler } from 'core/hooks'
import { NotUndefined } from 'core/types'

import BaseSelect, { CommonProps } from './BaseSelect'
import { findByValue } from './components'

export type SelectProps<Value> = Omit<
  CommonProps<false, Value>,
  'value' | 'isMulti'
> & {
  value: Value | null
  onChange?: PendingEventHandler<Value | null>
  isSearchable?: boolean
}

export const Select = <Value extends NotUndefined = string>({
  value,
  onChange,
  isSearchable,
  menuPlacement,
  ...props
}: SelectProps<Value>) => (
  <BaseSelect
    isSearchable={isSearchable}
    menuPlacement={menuPlacement}
    onChange={(option) => onChange?.(R.isNil(option) ? option : option.value)} // Workaround for JedWatson/react-select#5729
    value={R.isNil(value) ? value : findByValue(props.options, value)}
    {...props}
  />
)

export const SelectWithLabel = withLabel(Select)

export type MultiSelectProps<Value> = Omit<
  CommonProps<true, Value>,
  'value' | 'isMulti'
> & {
  value: Array<Value> | null
  onChange?: PendingEventHandler<Array<Value> | null>
  wrap?: boolean
  isSearchable?: boolean
}

export const MultiSelect = <Value extends NotUndefined = string>({
  value,
  onChange,
  isSearchable,
  ...props
}: MultiSelectProps<Value>) => (
  <BaseSelect
    isSearchable={isSearchable}
    onChange={(option) =>
      onChange?.(R.isNil(option) ? option : R.map(option, R.prop('value')))
    } // Workaround for JedWatson/react-select#5729
    value={
      R.isNil(value)
        ? value
        : R.pipe(
            value,
            R.map((v) => findByValue(props.options, v)),
            R.compact,
          )
    }
    {...props}
    isMulti
  />
)

export const MultiSelectWithLabel = withLabel(MultiSelect)
